import { Box } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { Plus } from '@sweep-io/sweep-design/dist/icons';
import React from 'react';
import { useSelector } from 'react-redux';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { selectDefaultCreationCrmOrgId, selectIsBuildModeEnabled } from '../../../reducers/userInfoReducer';
import { ActionsMenu, ActionsMenuAction } from '../../common/actions-menu/ActionsMenu';
import FancyTag from '../../common/FancyTag';
import { selectConnectedCrmOrgs } from '../../pages/environments/environmentsReducer';
import { AgentInputMode } from './agentInputModeTypes';
import { ArtifactIcon, BuildModeIcon, MultiOrgIcon, ResearchIcon } from './icons';
import MultiOrgFancyTagSelector from './MultiOrgFancyTagSelector';
import BetaTag from '../../common/BetaTag';

const BETA_MODES = [AgentInputMode.ExperimentalMode, AgentInputMode.DeepDiveMode, AgentInputMode.BuildMode];


type MenuDirection = 'up' | 'down';

interface AgentInputModeSelectorProps {
  selectedMode: AgentInputMode;
  onModeChange: (mode: AgentInputMode) => void;
  menuDirection?: MenuDirection;
}

const MODE_LABELS_MAP: Record<Exclude<AgentInputMode, AgentInputMode.None>, string> = {
  [AgentInputMode.ExperimentalMode]: 'Artifact',
  [AgentInputMode.DeepDiveMode]: 'Research',
  [AgentInputMode.BuildMode]: 'Build',
  [AgentInputMode.MultiOrgMode]: 'Multi-Org',
};

const MODE_ICON_MAP: Record<Exclude<AgentInputMode, AgentInputMode.None>, React.ReactNode> = {
  [AgentInputMode.ExperimentalMode]: <ArtifactIcon />,
  [AgentInputMode.DeepDiveMode]: <ResearchIcon />,
  [AgentInputMode.BuildMode]: <BuildModeIcon />,
  [AgentInputMode.MultiOrgMode]: <MultiOrgIcon />,
};

const MODE_SUB_LABELS_MAP: Record<Exclude<AgentInputMode, AgentInputMode.None>, string> = {
  [AgentInputMode.ExperimentalMode]: 'Build large output tables or structured artifacts',
  [AgentInputMode.DeepDiveMode]: 'Best for in-depth or complex discussions',
  [AgentInputMode.BuildMode]: 'Create Flows, Email Templates, and Fields',
  [AgentInputMode.MultiOrgMode]: 'Query across multiple environments',
};

const COLOR_MAP: Record<Exclude<AgentInputMode, AgentInputMode.None>, { [code: number]: string }> = {
  [AgentInputMode.ExperimentalMode]: colors.sun,
  [AgentInputMode.DeepDiveMode]: colors.blue,
  [AgentInputMode.BuildMode]: colors.lilac,
  [AgentInputMode.MultiOrgMode]: colors.mint,
};

const AgentInputModeSelector = ({ selectedMode, onModeChange, menuDirection = 'down' }: AgentInputModeSelectorProps) => {
  // Check if multi-org option should be shown (need >1 connected org)
  const connectedOrgs = useSelector(selectConnectedCrmOrgs);
  const currentOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const isBuildModeEnabled = useSelector(selectIsBuildModeEnabled);
  const { agentResearchMode: isAgentResearchModeEnabled, multiOrgQuery: isMultiOrgQueryEnabled } = useFeatureToggle();
  const hasMultipleOrgs = connectedOrgs.filter((org) => org.id !== currentOrgId).length > 0;

  const createActionMenuItem = ({ selectedMode }: { selectedMode: Exclude<AgentInputMode, AgentInputMode.None> }) => ({
    label: (
      <Box display="flex" alignItems="center" gap={2}>
        {MODE_ICON_MAP[selectedMode]}
        <Box display="flex" flexDirection="column" justifyContent="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body-bold">{MODE_LABELS_MAP[selectedMode]}</Typography>
            {BETA_MODES.includes(selectedMode) && <BetaTag />}
          </Box>
          <Typography variant="caption">{MODE_SUB_LABELS_MAP[selectedMode]}</Typography>
        </Box>
      </Box>
    ),
    value: selectedMode,
  })

  const actions: ActionsMenuAction<AgentInputMode>[] = [
    createActionMenuItem({ selectedMode: AgentInputMode.ExperimentalMode }),
    ...(isAgentResearchModeEnabled
      ? [createActionMenuItem({ selectedMode: AgentInputMode.DeepDiveMode })]
      : []),
    ...(isBuildModeEnabled
      ? [createActionMenuItem({ selectedMode: AgentInputMode.BuildMode })]
      : []),
    ...(hasMultipleOrgs && isMultiOrgQueryEnabled
      ? [createActionMenuItem({ selectedMode: AgentInputMode.MultiOrgMode })]
      : []),
  ];

  const handleActionClick = (action: ActionsMenuAction<AgentInputMode>) => {
    // If the same mode is selected, toggle it off
    if (action.value === selectedMode) {
      onModeChange(AgentInputMode.None);
    } else {
      onModeChange(action.value);
    }
  };

  const handleTagDelete = () => {
    onModeChange(AgentInputMode.None);
  };

  // For MultiOrgMode, the pill is rendered by MultiOrgSelector, not FancyTag
  const showFancyTag =
    selectedMode !== AgentInputMode.None && selectedMode !== AgentInputMode.MultiOrgMode;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <ActionsMenu
        actions={actions}
        onClick={handleActionClick}
        MenuButtonIcon={<Plus />}
        iconSize="small"
        iconVariant="flat"
        iconTooltip="Select advanced mode"
        menuWidth="350px"
        anchorOrigin={{
          vertical: menuDirection === 'up' ? -10 : 28,
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: menuDirection === 'up' ? 'bottom' : 'top',
          horizontal: 'left',
        }}
      />
      {showFancyTag && (
        <FancyTag
          text={MODE_LABELS_MAP[selectedMode]}
          colorVariant={COLOR_MAP[selectedMode]}
          onDelete={handleTagDelete}
          startIcon={MODE_ICON_MAP[selectedMode]}
          endAdornment={BETA_MODES.includes(selectedMode) ? <BetaTag /> : undefined}
        />
      )}
      {selectedMode === AgentInputMode.MultiOrgMode && <MultiOrgFancyTagSelector colorVariant={COLOR_MAP[selectedMode]} />}
    </Box>
  );
};

export default AgentInputModeSelector;
